import {EX} from "../exclaim";
import {KeySets} from "../utils/Keys";
import {isValidHttpUrl} from "../UrlValidation";
import {notSet, safeToNumber} from "../ValueUtils";
import {Classes, Types} from "./Constants";
import {Expressions} from "./Expressions";

// FUTURE: eliminate this and just use ValidationResult instead
export interface InternalValidationResult {
    readonly isValid: boolean;
    readonly message?: string;
}

interface MandatoryDetails {
    readonly result: boolean,
    readonly isMandatory: boolean
}

export class Validator {
    private readonly _element: HTMLElement;
    private readonly types: string[];
    private readonly isEdjuster: boolean;

    get element(): HTMLElement {
        return this._element;
    }

    constructor(element: HTMLElement, types: string[]) {
        this._element = element;
        this.types = types || [];
        this.isEdjuster = EX.isEdjuster || false;
    }

    init() {
        this.initMandatoryFields();
        this.initListeners();
    }

    private initMandatoryFields() {
        // Add a CSS class to the mandatory fields to identify them
        // (not sure why we need this, but the tests rely on it)
        if ((this.types.includes(Types.MANDATORY) ||
                (this.types.includes(Types.MANDATORY_EDJUSTER) && this.isEdjuster)) ||
            (this.types.includes(Types.WARNING))
        ) {
            this.element.classList.add(Classes.MANDATORY);
        }
    }

    private initListeners() {
        this.element.addEventListener("keypress", (e) => this.onKeyPress(e));
        this.element.addEventListener("blur", () => this.onBlur());
        this.element.addEventListener("change", (e) => this.onChange(e));
    }

    private onKeyPress(e: KeyboardEvent) {
        const el = e.target as HTMLInputElement;
        if (el.tagName !== "INPUT") {
            return;
        }

        el.classList.remove(Classes.ERROR);

        // Check for any "special keys" that cannot be used as input data directly
        // (basically any key that isn't an input character has a multi-character string assigned,
        // however, I have no idea how this might work or not work with non-latin character sets).
        // see https://developer.mozilla.org/en-US/docs/Web/API/KeyboardEvent/key#value
        if (e.key.length > 1) {
            return;
        }

        let proposedValue: string;
        // Check if the value has text selected (the '|| 0' is to handle null / undefined cases)
        if ((el.selectionStart || 0) === 0 && (el.selectionEnd || 0) === 0) {
            // Append to the end of the text we are evaluating
            proposedValue = el.value + e.key;
        } else {
            // Replace selected text with the key.
            proposedValue =
                el.value.slice(0, el.selectionStart) +
                e.key +
                el.value.slice(el.selectionEnd);
        }

        if (this.types.includes(Types.INTEGER) &&
            KeySets.NUMBERS.includes(e.key) &&
            !Expressions.validate(Expressions.INTEGER, proposedValue, el, false)
        ) {
            e.preventDefault();
        }
    }

    private onBlur() {
        this.validate();
    }

    private onChange(e: Event) {
        const el = e.target as HTMLElement;
        if (el.tagName === "INPUT") {
            // This behaviour is a little bit wierd, marking a checkbox as invalid if not checked.
            // Not sure why that would be the case. I suspect we don't use this anywhere, but I
            // could be incorrect.
            const inputEl = el as HTMLInputElement;
            if (inputEl.type === "checkbox" && !inputEl.checked) {
                el.classList.add(Classes.ERROR);
                return;
            }
        }

        // Something changed, so clear any error until we next check (on blur typically)
        // Note that I think this deviates from standard HTML 5 validation.
        el.classList.remove(Classes.ERROR);
    }

    private hasValueAttribute(): boolean {
        return "value" in this.element;
    }

    private isMandatoryOrWarn(): MandatoryDetails {
        const el = this.element;
        const hasValueAttribute = this.hasValueAttribute();
        const value = hasValueAttribute ? (el as HTMLInputElement).value : null;
        const text = hasValueAttribute ? null : el.innerText;
        const isCheckbox = el.tagName === "INPUT" && (el as HTMLInputElement).type === "checkbox";
        const isChecked = isCheckbox && (el as HTMLInputElement).checked;

        const isMandatory = Boolean(
            this.types.includes(Types.MANDATORY) ||
            (this.types.includes(Types.MANDATORY_EDJUSTER) && this.isEdjuster)
        );
        const isWarning = Boolean(this.types.includes(Types.WARNING));

        return {
            result: Boolean((isMandatory || isWarning) && (
                (hasValueAttribute && (!value || value.length <= 0)) ||
                (!hasValueAttribute && (!text || text.length <= 0)) ||
                (isCheckbox && !isChecked)
            )),
            // only fails if isMandatory -> returns false to fail
            isMandatory: isMandatory,
        };
    }

    validateEmail(value: string): boolean {
        if (!this.types.includes(Types.EMAIL)) {
            return true;
        }

        return Expressions.validateEmail(value, this.element, true);
    }

    validateEmailWithHandleBars(value: string): boolean {
        if (!this.types.includes(Types.EMAIL_WITH_HANDLEBARS)) {
            return true;
        }

        // This isn't properly comprehensive in terms of replacement,
        // but it should do for most of the common cases we expect to have
        const replaced_handle_bars = value.replace(/\{{2}[^{}]*\}{2}/gu, 'a');
        return Expressions.validateEmail(replaced_handle_bars, this.element, true);
    }

    validateHyperlink(value: string): boolean {
        if (!this.types.includes(Types.HYPERLINK)) {
            return true;
        }

        return Expressions.validate(Expressions.HYPERLINK, value, this.element, true);
    }

    validateUrl(value: string): boolean {
        if (!this.types.includes(Types.URL)) {
            return true;
        }

        const el = this.element;

        if (!isValidHttpUrl(value)) {
            el.classList.add(Classes.ERROR);
            return false;
        }
        return true;
    }

    validateFileName(value: string): boolean {
        if (!this.types.includes(Types.FILE_NAME)) {
            return true;
        }

        return Expressions.validate(Expressions.FILE_NAME, value, this.element, true);
    }

    validateIpAddressRange(value: string): boolean {
        if (!this.types.includes(Types.IP_ADDRESS_RANGE)) {
            return true;
        }

        return Expressions.validateIpAddressRange(value, this.element, true);
    }

    validateIpAddressRanges(value: string): boolean {
        if (!this.types.includes(Types.IP_ADDRESS_RANGES)) {
            return true;
        }

        return Expressions.validateIpAddressRanges(value, this.element, true);
    }

    validatePositiveNumber(value: string): boolean {
        if (!this.types.includes(Types.POSITIVE_NUMBER)) {
            return true;
        }

        return Expressions.validate(Expressions.POSITIVE_NUMBER, value, this.element, true);
    }

    validateBigDecimal(value: string): boolean {
        if (!this.types.includes(Types.BIG_DECIMAL)) {
            return true;
        }

        return Expressions.validate(Expressions.BIG_DECIMAL, value, this.element, true);
    }

    validateInteger(value: string): boolean {
        if (!this.types.includes(Types.INTEGER)) {
            return true;
        }

        return Expressions.validate(Expressions.INTEGER, value, this.element, true);
    }

    private getHybridAttribute(dataAttributeName: string): string {
        // Hybrid implementation to allow JSDOM to work in unit tests
        if (typeof this.element.dataset !== 'undefined') {
            if (dataAttributeName in this.element.dataset) {
                return this._element.dataset[dataAttributeName];
            }
        }

        // Retrieve as a regular attribute (if possible)
        return this.element.getAttribute(dataAttributeName);
    }

    validateNumberRange(valString: string): InternalValidationResult {
        if (!this.types.includes(Types.NUMBER_RANGE)) {
            return { isValid: true };
        }

        const el = this.element;

        const min = safeToNumber(this.getHybridAttribute("min"));
        const max = safeToNumber(this.getHybridAttribute("max"));

        const val = safeToNumber(valString);
        if (valString && notSet(val)) {
            // We have a value, but we couldn't convert it to a number
            el.classList.add(Classes.ERROR);
            return { isValid: false };
        }

        if (val < min || val > max) {
            el.classList.add(Classes.ERROR);
        }
        if (val < min) {
            // Return a more descriptive failure error message for the user
            return {
                isValid: false,
                message: EX.Messages.prop(
                    "Field_min_value_error",
                    EX.Messages.prop("GenericFieldName"),
                    val,
                    min
                )
            };
        }
        if (val > max) {
            // Return a more descriptive failure error message for the user
            return {
                isValid: false,
                message: EX.Messages.prop(
                    "Field_max_value_error",
                    EX.Messages.prop("GenericFieldName"),
                    val,
                    max
                )
            };
        }

        return { isValid: true };
    }

    validateDate(value: string): boolean {
        if (!this.types.includes(Types.DATE)) {
            return true;
        }

        const el = this.element;

        // First let's validate it with a regex
        const matches = Expressions.validateRegExResult(Expressions.DATE, value, el, true);
        if (!matches) {
            return false;
        }

        const year = Number(matches[1]),
            month = Number(matches[2]),
            day = Number(matches[3]);
        if ((month === 4 || month === 6 || month === 9 || month === 11) && day === 31) {
            // Verify if the selected month has a 31st
            el.classList.add(Classes.ERROR);
        } else if (month === 2) {
            // Check for february 29th on leap years
            const isLeapYear = (year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0));
            if (day > 29 || (day === 29 && !isLeapYear)) {
                el.classList.add(Classes.ERROR);
            }
        }
        return false;
    }

    validateHtml5(): boolean {
        if (!this.types.includes(Types.HTML5)) {
            return true;
        }

        const el = this.element;

        if (el && !(el as HTMLInputElement).reportValidity()) {
            el.classList.add(Classes.ERROR);
            return false;
        }

        return true;
    }

    inError(): boolean {
        return this.element.classList.contains(Classes.ERROR);
    }

    /**
     * Validates this specific validator based on the configured types.
     *
     * @returns If a string is provided in message, then that is an error
     * message that can be displayed to a user. If isValid === true, validation was successful,
     * otherwise isValid === false indicates validation failed.
     */
    validate(): InternalValidationResult {
        const el = this.element;

        // Let's remove the error class if it's present and re-validate the field
        el.classList.remove(Classes.ERROR);

        const value = (el as HTMLInputElement).value;
        const isValidationDisabled = el.getAttribute("do-validation") === "false";
        if (isValidationDisabled) {
            return { isValid: true };
        }

        // Validate "mandatory-ness"
        const mandatory = this.isMandatoryOrWarn();
        if (mandatory.result) {
            // Field has no content as is either mandatory, or is flagged to warn if empty
            el.classList.add(Classes.ERROR);
            return { isValid: !mandatory.isMandatory };
        }

        // Check if we have any content to validate
        if (!value || value.length <= 0) {
            // Note that not all HTML 5 input elements have a "value"
            // set when partial input data is present, so we do this
            // even if there is no value set
            this.validateHtml5();
            return { isValid: !this.inError() };
        }

        this.validateEmail(value);
        this.validateEmailWithHandleBars(value);
        this.validateHyperlink(value);
        this.validateUrl(value);
        this.validateFileName(value);
        this.validateIpAddressRange(value);
        this.validateIpAddressRanges(value);
        this.validatePositiveNumber(value);
        this.validateBigDecimal(value);
        this.validateInteger(value);
        const r = this.validateNumberRange(value);
        if (!r.isValid) {
            return r;
        }
        this.validateDate(value);
        this.validateHtml5();

        return { isValid: !this.inError() };
    }
}
